import type { QuoteScreenPrintResult } from '@fsp/contracts';
import { Check, Copy, GitCompare, Pin, PinOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  clipboardSummary,
  currency,
  MARGIN_THRESHOLDS,
  marginTone,
  quoteLabel,
  totalQty,
  type UiValues,
} from './_shared';
import {
  addPinned,
  clearPinned,
  loadPinned,
  MAX_PINNED,
  type PinnedScenario,
  removePinned,
} from './calc-pinned';

// ─── Pin button (action bar) ────────────────────────────────────────────────

/**
 * Renders in the result panel's action bar. Pins the live (ready)
 * calculation as a PinnedScenario so the user can swap to another
 * scenario and compare side-by-side.
 */
export function PinButton({
  values,
  result,
  onChange,
}: {
  values: UiValues;
  result: QuoteScreenPrintResult;
  onChange: (list: PinnedScenario[]) => void;
}) {
  const [flashed, setFlashed] = useState(false);
  useEffect(() => {
    if (!flashed) return;
    const t = window.setTimeout(() => setFlashed(false), 1200);
    return () => window.clearTimeout(t);
  }, [flashed]);

  function handleClick() {
    const next = addPinned({
      at: Date.now(),
      values,
      summary: {
        label: quoteLabel(values),
        perUnitSell: result.perUnitSell,
        orderTotal: result.orderTotal,
        marginPct: result.marginPct,
        totalQty: totalQty(values),
        mode: result.mode,
      },
    });
    onChange(next);
    setFlashed(true);
  }

  return (
    <Button type="button" variant="ghost" size="sm" onClick={handleClick} className="gap-1.5">
      {flashed ? (
        <>
          <Check className="size-3.5 text-emerald-600" />
          <span>Pinned</span>
        </>
      ) : (
        <>
          <Pin className="size-3.5" />
          <span>Pin</span>
        </>
      )}
    </Button>
  );
}

// ─── Comparison bar (below result panel) ────────────────────────────────────

/**
 * Sticky-ish bar that surfaces pinned scenarios beneath the result
 * panel. Each pin is a compact card with sell/total/margin; hover to
 * see the × remove button. The Compare button opens the side-by-side
 * drawer. Hidden entirely when nothing is pinned.
 */
export function ComparisonBar({
  form,
  liveValues,
  liveResult,
  pinned,
  onChange,
}: {
  form: UseFormReturn<UiValues>;
  liveValues: UiValues;
  liveResult: QuoteScreenPrintResult | null;
  pinned: PinnedScenario[];
  onChange: (list: PinnedScenario[]) => void;
}) {
  const [compareOpen, setCompareOpen] = useState(false);

  if (pinned.length === 0) return null;

  function handleRemove(id: string) {
    onChange(removePinned(id));
  }
  function handleRestore(scenario: PinnedScenario) {
    form.reset(scenario.values, { keepDefaultValues: true });
  }
  function handleClear() {
    clearPinned();
    onChange([]);
  }

  return (
    <div className="mt-4 space-y-3 rounded-xl border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Pinned · {pinned.length}/{MAX_PINNED}
        </h2>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 text-xs"
            onClick={() => setCompareOpen(true)}
            disabled={pinned.length === 0}
          >
            <GitCompare className="size-3.5" />
            Compare
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-xs text-muted-foreground"
            onClick={handleClear}
          >
            Clear
          </Button>
        </div>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {pinned.map((p) => (
          <PinCard
            key={p.id}
            pin={p}
            liveValues={liveValues}
            onRestore={() => handleRestore(p)}
            onRemove={() => handleRemove(p.id)}
          />
        ))}
      </ul>

      <ComparisonSheet
        open={compareOpen}
        onOpenChange={setCompareOpen}
        liveValues={liveValues}
        liveResult={liveResult}
        pinned={pinned}
      />
    </div>
  );
}

function PinCard({
  pin,
  liveValues,
  onRestore,
  onRemove,
}: {
  pin: PinnedScenario;
  liveValues: UiValues;
  onRestore: () => void;
  onRemove: () => void;
}) {
  const diff = diffVsLive(pin.values, liveValues);
  return (
    <li className="group relative">
      <button
        type="button"
        onClick={onRestore}
        className="flex w-full flex-col items-start gap-1 rounded-md border bg-background p-3 text-left transition-colors hover:bg-muted/40"
        title="Restore this scenario"
      >
        <span className="truncate text-sm font-medium" title={pin.summary.label}>
          {pin.summary.label}
        </span>
        <span className="flex items-center gap-2 text-[11px] text-muted-foreground tabular-nums">
          <span>{currency(pin.summary.perUnitSell)}/ea</span>
          <span>·</span>
          <span>{currency(pin.summary.orderTotal)}</span>
          <span>·</span>
          <span className={marginToneClass(pin.summary.marginPct)}>
            {pin.summary.marginPct.toFixed(0)}%
          </span>
          <span className="uppercase">{pin.summary.mode}</span>
        </span>
        {diff && (
          <span className="line-clamp-1 w-full text-[11px] text-muted-foreground/80" title={diff}>
            vs live: {diff}
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute right-1 top-1 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100"
        aria-label={`Remove ${pin.summary.label}`}
      >
        <X className="size-3.5" />
      </button>
    </li>
  );
}

// ─── Comparison side-by-side sheet ──────────────────────────────────────────

/**
 * Full drawer showing current (live) + each pinned scenario as
 * columns. Each row is a comparison metric: per-unit sell, total,
 * margin, floor, setup fee, qty. Min-value cells are tinted emerald
 * so "cheapest per-unit" and "highest margin" jump out.
 */
function ComparisonSheet({
  open,
  onOpenChange,
  liveValues,
  liveResult,
  pinned,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  liveValues: UiValues;
  liveResult: QuoteScreenPrintResult | null;
  pinned: PinnedScenario[];
}) {
  const columns = buildColumns(liveValues, liveResult, pinned);
  const rows = buildRows(columns);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-3xl">
        <SheetHeader className="space-y-1 pb-4">
          <SheetTitle className="flex items-center gap-2">
            <GitCompare className="size-4" />
            Compare scenarios
          </SheetTitle>
          <SheetDescription>
            Side-by-side view of your pinned scenarios plus the current live calc. Best-in-row
            values are highlighted.
          </SheetDescription>
        </SheetHeader>

        {columns.length <= 1 ? (
          <p className="rounded-md border border-dashed bg-muted/20 p-4 text-center text-xs text-muted-foreground">
            Pin at least one scenario, then change the form to compare a second.
          </p>
        ) : (
          <div className="space-y-4">
            <CopyComparisonButton columns={columns} />
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                    <th className="px-3 py-2 font-semibold">Metric</th>
                    {columns.map((col) => (
                      <th key={col.key} className="px-3 py-2 font-semibold">
                        <div className="flex items-baseline gap-1">
                          <span className="truncate" title={col.label}>
                            {col.label}
                          </span>
                          {col.live && (
                            <span className="text-[9px] uppercase tracking-wide text-primary">
                              Live
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.label} className="border-b last:border-0">
                      <th className="whitespace-nowrap bg-muted/10 px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                        {row.label}
                      </th>
                      {row.cells.map((cell) => (
                        <td
                          key={`${row.label}-${cell.key}`}
                          className={cn(
                            'px-3 py-2 tabular-nums',
                            cell.best && 'bg-emerald-500/10 font-medium text-emerald-700',
                          )}
                        >
                          {cell.text}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

type ScenarioColumn = {
  /** Stable id (live uses a fixed token). */
  key: string;
  /** Short header label ("Gildan 5000 Black · 72"). */
  label: string;
  /** Whether this is the current live scenario. */
  live: boolean;
  /** Underlying form values — used for clipboard export. */
  values: UiValues;
  /** Engine-priced figures for the scenario; null when the live calc isn't ready. */
  summary: {
    perUnitSell: number;
    orderTotal: number;
    marginPct: number;
    totalQty: number;
    mode: 'b2c' | 'b2b';
  } | null;
  /** Full engine result for deeper metrics (live only — pins just store summary). */
  result: QuoteScreenPrintResult | null;
};

function buildColumns(
  liveValues: UiValues,
  liveResult: QuoteScreenPrintResult | null,
  pinned: PinnedScenario[],
): ScenarioColumn[] {
  const live: ScenarioColumn = {
    key: 'live',
    label: quoteLabel(liveValues),
    live: true,
    values: liveValues,
    summary: liveResult
      ? {
          perUnitSell: liveResult.perUnitSell,
          orderTotal: liveResult.orderTotal,
          marginPct: liveResult.marginPct,
          totalQty: liveResult.totalQty,
          mode: liveResult.mode,
        }
      : null,
    result: liveResult,
  };
  const pinnedCols: ScenarioColumn[] = pinned.map((p) => ({
    key: p.id,
    label: p.summary.label,
    live: false,
    values: p.values,
    summary: p.summary,
    result: null,
  }));
  return [live, ...pinnedCols];
}

type Row = {
  label: string;
  cells: { key: string; text: string; best: boolean }[];
};

/**
 * Build the comparison rows with best-of-row tinting. "Best" depends
 * on metric direction: lower is better for price, higher is better
 * for margin. Setup fee only shows if at least one column provides
 * it (i.e., the live column — pins don't store full breakdowns).
 */
function buildRows(columns: ScenarioColumn[]): Row[] {
  const perUnitSells = columns.map((c) => c.summary?.perUnitSell);
  const orderTotals = columns.map((c) => c.summary?.orderTotal);
  const margins = columns.map((c) => c.summary?.marginPct);

  const minValid = (nums: (number | undefined)[]) =>
    nums.reduce<number | undefined>(
      (acc, n) => (n === undefined ? acc : acc === undefined || n < acc ? n : acc),
      undefined,
    );
  const maxValid = (nums: (number | undefined)[]) =>
    nums.reduce<number | undefined>(
      (acc, n) => (n === undefined ? acc : acc === undefined || n > acc ? n : acc),
      undefined,
    );

  const minSell = minValid(perUnitSells);
  const minTotal = minValid(orderTotals);
  const maxMargin = maxValid(margins);

  const markBest = (v: number | undefined, target: number | undefined) =>
    v !== undefined && target !== undefined && columns.length > 1 && v === target;

  return [
    {
      label: 'Per unit sell',
      cells: columns.map((c) => ({
        key: c.key,
        text: c.summary ? currency(c.summary.perUnitSell) : '—',
        best: markBest(c.summary?.perUnitSell, minSell),
      })),
    },
    {
      label: 'Order total',
      cells: columns.map((c) => ({
        key: c.key,
        text: c.summary ? currency(c.summary.orderTotal) : '—',
        best: markBest(c.summary?.orderTotal, minTotal),
      })),
    },
    {
      label: 'Margin',
      cells: columns.map((c) => ({
        key: c.key,
        text: c.summary ? `${c.summary.marginPct.toFixed(1)}%` : '—',
        best: markBest(c.summary?.marginPct, maxMargin),
      })),
    },
    {
      label: 'Quantity',
      cells: columns.map((c) => ({
        key: c.key,
        text: c.summary ? String(c.summary.totalQty) : '—',
        best: false,
      })),
    },
    {
      label: 'Mode',
      cells: columns.map((c) => ({
        key: c.key,
        text: c.summary ? c.summary.mode.toUpperCase() : '—',
        best: false,
      })),
    },
    {
      label: 'Floor / unit',
      cells: columns.map((c) => ({
        key: c.key,
        text: c.result ? currency(c.result.floorPerUnit) : c.live ? '—' : '(live only)',
        best: false,
      })),
    },
  ];
}

function CopyComparisonButton({ columns }: { columns: ScenarioColumn[] }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  async function handleCopy() {
    const lines = columns
      .filter((c) => c.summary)
      .map((c) => {
        const summary = c.summary;
        if (!summary) return '';
        return clipboardSummary(c.values, {
          perUnitSell: summary.perUnitSell,
          orderTotal: summary.orderTotal,
          marginPct: summary.marginPct,
          mode: summary.mode,
        });
      })
      .filter(Boolean);
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // noop
    }
  }

  return (
    <div className="flex items-center justify-end">
      <Button type="button" variant="outline" size="sm" onClick={handleCopy} className="gap-1.5">
        {copied ? (
          <>
            <Check className="size-3.5 text-emerald-600" />
            <span>Copied</span>
          </>
        ) : (
          <>
            <Copy className="size-3.5" />
            <span>Copy comparison</span>
          </>
        )}
      </Button>
    </div>
  );
}

// ─── Empty-state helper (exposed for the page chrome) ───────────────────────

export function PinEmptyState() {
  return (
    <div className="mt-4 flex items-center gap-2 rounded-md border border-dashed bg-muted/10 p-3 text-xs text-muted-foreground">
      <PinOff className="size-3.5" />
      <span>Pin a ready quote to compare it against another. Up to {MAX_PINNED} at a time.</span>
    </div>
  );
}

/**
 * Compact one-liner describing how a pinned scenario differs from the
 * live form. Shows up to three deltas — qty, garment, mode, locations,
 * discount — in priority order. Returns `null` when nothing meaningful
 * differs (the pin is essentially a no-op). The signs are pin-relative:
 * "qty +24" means the pin has 24 more units than live.
 */
function diffVsLive(pin: UiValues, live: UiValues): string | null {
  const parts: string[] = [];

  const qtyPin = totalQty(pin);
  const qtyLive = totalQty(live);
  if (qtyPin !== qtyLive) {
    const delta = qtyPin - qtyLive;
    parts.push(`qty ${delta > 0 ? '+' : ''}${delta}`);
  }

  if (pin.garmentSource !== live.garmentSource) {
    parts.push(`source → ${labelSource(pin.garmentSource)}`);
  } else if (
    pin.garmentSource === 'we_provide_by_style' &&
    pin.style.trim() &&
    pin.style.trim() !== live.style.trim()
  ) {
    parts.push(`→ ${pin.style.trim()}`);
  }

  if (pin.mode !== live.mode) {
    parts.push(`${pin.mode.toUpperCase()}`);
  }

  const pinLocs = locationSet(pin);
  const liveLocs = locationSet(live);
  const added = [...pinLocs].filter((l) => !liveLocs.has(l));
  const removed = [...liveLocs].filter((l) => !pinLocs.has(l));
  if (added.length) parts.push(`+${added.join('+')}`);
  if (removed.length) parts.push(`−${removed.join('−')}`);

  if (pin.discountPct !== live.discountPct) {
    parts.push(`disc ${pin.discountPct}%`);
  }

  if (pin.rush !== live.rush) {
    parts.push(pin.rush === 'none' ? 'no rush' : pin.rush === 'rush' ? 'rush' : 'hot rush');
  }

  if (parts.length === 0) return null;
  return parts.slice(0, 3).join(', ');
}

function locationSet(v: UiValues): Set<string> {
  const s = new Set<string>();
  if (v.frontEnabled) s.add('front');
  if (v.backEnabled) s.add('back');
  if (v.sleeveEnabled) s.add('sleeve');
  return s;
}

function labelSource(s: UiValues['garmentSource']): string {
  if (s === 'we_provide_by_style') return 'SS';
  if (s === 'we_provide') return 'we provide';
  return 'customer';
}

function marginToneClass(marginPct: number): string {
  const tone = marginTone(marginPct);
  if (tone === 'positive') return 'text-emerald-600';
  if (tone === 'negative') return 'text-destructive';
  return 'text-amber-600';
}

// Not used but surfacing the threshold constant keeps the import from
// showing as unused when the module is consumed piecemeal.
export { loadPinned, MARGIN_THRESHOLDS };
